import { useState, useEffect } from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip, Button,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  AttachMoney as SalaryIcon,
  Work as WorkIcon,
  SmartToy as SmartToyIcon,
  ContentCopy as DuplicateIcon,
  Link as LinkIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { WhatsApp as WhatsAppIcon, CampaignOutlined as CampaignIcon } from '@mui/icons-material';
import api from '../../config/api';
import useStore from '../../config/store';
import AtsDeadlineChip from './AtsDeadlineChip';
import AtsCampaignDialog from './AtsCampaignDialog';
import AtsDeletePositionDialog from './AtsDeletePositionDialog';
import { getStatusColor, getStatusLabel, isVisibleInPortal } from './positionStatus';

const AtsPositionHeader = ({ position, positionId, canEdit = true, onBack, onEdit, onOpenPortal, onOpenSimulator, onDuplicate, onDeleted, formatUbicacion, formatSalario }) => {
  const { authToken, showAlert } = useStore.getState();

  // Estado de la captación (badge de pausa por límite) — solo masivas
  const [captationStatus, setCaptationStatus] = useState(null);
  useEffect(() => {
    setCaptationStatus(null);
    if (position?.tipo !== 'massive' || !positionId) return;
    let alive = true;
    api.get(`/captation/${positionId}/status`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then(({ data }) => {
        if (alive && data.response?.status_response === 'OK') setCaptationStatus(data.response.data);
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId, position?.tipo]);

  // Copiar link wa.me con la vacante pre-cargada ([REF:positionId])
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // El número es el de la organización de la vacante (2026-10-26): el propio,
  // el compartido de Grafton si un super-admin lo permitió, o ninguno.
  // showAlert(mensaje, severidad): antes iban al revés y el aviso se veía mal.
  const handleCopyWhatsappLink = async () => {
    try {
      const { data } = await api.get('/captation/whatsapp-info', {
        params: { organizationId: position?.organizationId || useStore.getState().organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const number = data.response?.data?.number;
      if (!number) {
        showAlert(t('ats.captation.whatsappLink.noNumber'), 'error');
        return;
      }
      const text = `¡Hola! Vengo por el cargo ${position?.name || ''} [${positionId}]`;
      const link = `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
      await navigator.clipboard.writeText(link);
      showAlert(t('ats.captation.whatsappLink.copied'), 'success');
    } catch (err) {
      console.error('Error copiando link de WhatsApp:', err);
      showAlert(t('ats.captation.whatsappLink.error'), 'error');
    }
  };

  // Masivas: su canal público es el chat de captación (/postular/:id), no el
  // portal (que las rechaza por diseño) → en vez de abrir el portal se copia el link del chat.
  const handleCopyChatLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/postular/${positionId}`);
      showAlert(t('ats.captation.chatLink.copied'), 'success');
    } catch (err) {
      console.error('Error copiando link del chat:', err);
      showAlert(t('ats.captation.chatLink.error'), 'error');
    }
  };
  const { t } = useTranslation();


  return (
    <Box sx={{
      p: 3,
      borderBottom: 1,
      borderColor: 'divider',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    }}>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          color="inherit"
          size="small"
          sx={{ mb: 1, ml: -1 }}
        >
          {t('ats.positions.title')}
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h4" fontWeight={700}>
            {position?.internalName || position?.name}
          </Typography>

          <Chip
            label={getStatusLabel(position?.status, t)}
            color={getStatusColor(position?.status)}
            size="small"
          />

          <AtsDeadlineChip position={position} />

          {position?.visibility === 'confidential' && (
            <Chip label="Confidencial" color="warning" size="small" variant="outlined" />
          )}

          {position?.tipo && (
            <Chip
              label={t(`ats.positions.wizard.options.tipo.${position.tipo}`)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {position?.internalName && position.internalName !== position.name && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('ats.detail.publicNameLabel')}: {position?.name}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
          {position?.industria && (
            <Typography variant="body2" color="text.secondary">{position.industria}</Typography>
          )}

          {formatUbicacion(position?.ubicacion) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {formatUbicacion(position.ubicacion)}
              </Typography>
            </Box>
          )}

          {position?.modalidad && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WorkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {t(`ats.positions.wizard.options.modalidad.${position.modalidad}`)}
              </Typography>
            </Box>
          )}

          {position?.salarioRango?.visible && formatSalario(position.salarioRango) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <SalaryIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {formatSalario(position.salarioRango)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {canEdit && (
          <Tooltip title={t('ats.detail.edit')}>
            <IconButton size="small" onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Simulador del bot de captación — solo vacantes de reclutamiento masivo */}
        {captationStatus?.limitReached && (
          <Tooltip
            title={t('ats.captation.limitBadge.tooltip', {
              count: captationStatus.botApplications,
              limit: captationStatus.limit,
            })}
            arrow
          >
            <Chip
              size="small"
              color="warning"
              label={t('ats.captation.limitBadge.label')}
              sx={{ mr: 0.5 }}
            />
          </Tooltip>
        )}

        {onDuplicate && (
          <Tooltip title={t('ats.detail.duplicate')}>
            <IconButton size="small" onClick={onDuplicate}>
              <DuplicateIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {onOpenSimulator && position?.tipo === 'massive' && (
          <Tooltip title={t('ats.captation.simulatorButton')}>
            <IconButton size="small" onClick={onOpenSimulator}>
              <SmartToyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {position?.tipo === 'massive' && (
          <Tooltip title={t('ats.captation.whatsappLink.button')}>
            <IconButton size="small" onClick={handleCopyWhatsappLink}>
              <WhatsAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {position?.tipo === 'massive' && (
          <Tooltip title={t('ats.captation.campaigns.headerButton')}>
            <IconButton size="small" onClick={() => setCampaignOpen(true)}>
              <CampaignIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {position?.tipo === 'massive' && (
          <Tooltip title={t('ats.captation.chatLink.button')}>
            <IconButton size="small" onClick={handleCopyChatLink}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Portal: solo con "Visible en portal" encendido (hunting y masivas);
            apagado, el link muestra "no disponible" (PLAN_MASIVO_PORTAL) */}
        {isVisibleInPortal(position) && (
          <Tooltip title={t('ats.detail.openPortal')}>
            <IconButton size="small" onClick={onOpenPortal}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {canEdit && onDeleted && (
          <Tooltip title={t('ats.positions.deleteDialog.button')}>
            <IconButton size="small" onClick={() => setDeleteOpen(true)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <AtsCampaignDialog
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        positionId={positionId}
      />

      {canEdit && onDeleted && (
        <AtsDeletePositionDialog
          open={deleteOpen}
          positionId={positionId}
          positionName={position?.internalName || position?.name}
          candidatesCount={position?.totalApplications || 0}
          onClose={() => setDeleteOpen(false)}
          onDeleted={onDeleted}
        />
      )}
    </Box>
  );
};

export default AtsPositionHeader;